import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiChevronDown, HiX, HiPencil } from 'react-icons/hi';
import { FaCoins } from 'react-icons/fa';
import Toast from '../../components/toaster';
import { getKarmaCoinsConfig, updateKarmaCoinsConfig } from '../../services/karmaService';
import KrishnaTimePlansTab from './KrishnaTimePlansTab';

// Cache for karma coins config data
const karmaCoinsCache = {
  config: null,
  firstLoad: true,
};

// Keys allowed per card — only these are sent on Save (backend rejects unknown fields)
const CARD_FIELD_KEYS = {
  points: ['points_threshold', 'points_reward'],
  lessons: ['lessons_threshold', 'lessons_reward'],
  audio: ['audio_lessons_threshold', 'audio_lessons_reward'],
  not_eligible_reward: ['not_eligible_reward'],
  referral: ['referral_reward'],
  life: ['life_refill_reward', 'life_reward'],
  full_life: ['life_coins_reward'],
  double_coins_event: ['double_coins_event_required_chapter', 'double_coins_event_redumption'],
  rank1: ['leaderboard_rank_1_reward'],
  rank2: ['leaderboard_rank_2_reward'],
  rank3: ['leaderboard_rank_3_reward'],
  others: ['leaderboard_others_reward'],
};

const KarmaCoins = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(karmaCoinsCache.firstLoad);

  const defaultConfig = {
    not_eligible_reward: 0,
    points_threshold: 0,
    points_reward: 0,
    lessons_threshold: 5,
    lessons_reward: 10,
    audio_lessons_threshold: 3,
    audio_lessons_reward: 10,
    referral_reward: 100,
    leaderboard_rank_1_reward: 100,
    leaderboard_rank_2_reward: 80,
    leaderboard_rank_3_reward: 50,
    leaderboard_others_reward: 10,
    life_refill_reward: 2,
    life_reward: 2,
    life_coins_reward: 100,
    double_coins_event_required_chapter: 0,
    double_coins_event_redumption: 0
  };

  const [config, setConfig] = useState(karmaCoinsCache.config || defaultConfig);

  const [editingCard, setEditingCard] = useState(null);
  const [tempConfig, setTempConfig] = useState({});
  const tabFromUrl = searchParams.get('tab') || 'rewards';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const tabs = [
    { id: 'rewards', name: 'Rewards Configuration' },
    { id: 'referral', name: 'Referral Rewards' },
    { id: 'leaderboard', name: 'Leaderboard Rewards' },
    { id: 'time-plans', name: 'Krishna Time Plans' },
  ];

  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false);

  // Sync activeTab from URL params when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const currentTabFromUrl = searchParams.get('tab') || 'rewards';
    if (['rewards', 'referral', 'leaderboard', 'time-plans'].includes(currentTabFromUrl)) {
      setActiveTab(prevTab => {
        // Only update if URL tab is different from current state
        if (prevTab !== currentTabFromUrl) {
          return currentTabFromUrl;
        }
        return prevTab;
      });
    }
  }, [searchParams]);

  useEffect(() => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }

    const hasCache = karmaCoinsCache.config !== null && !karmaCoinsCache.firstLoad;
    
    // On initial mount, if we have cached data, use it immediately
    if (hasCache && isInitialMountRef.current) {
      setConfig(karmaCoinsCache.config);
      setIsFirstTime(false);
      isInitialMountRef.current = false;
      // Still fetch fresh data in background (but don't show loader since firstLoad is false)
      fetchConfig();
      return;
    }
    
    // Only fetch on initial mount
    if (isInitialMountRef.current) {
      // First time load - show loader
      fetchConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }

    const isFirstLoad = karmaCoinsCache.firstLoad;
    
    // Only show loader on first load
    if (isFirstLoad) {
      setLoading(true);
    }

    isLoadingRef.current = true;
    
    try {
      const data = await getKarmaCoinsConfig();
      if (data && data.data) {
        setConfig(data.data);
        setIsFirstTime(false);
        // Update cache
        karmaCoinsCache.config = data.data;
        karmaCoinsCache.firstLoad = false;
      } else {
        setIsFirstTime(true);
        karmaCoinsCache.firstLoad = false;
      }
    } catch (error) {
      setIsFirstTime(true);
      karmaCoinsCache.firstLoad = false;
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      // Mark initial mount as complete after first API call
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
    }
  };

  const handleEdit = (cardType) => {
    setEditingCard(cardType);
    setTempConfig({ ...config });
    setFieldErrors({});
  };

  const handleCancel = () => {
    setEditingCard(null);
    setTempConfig({});
    setFieldErrors({});
  };

  const handleSave = async () => {
    // Validate life_reward field before saving
    if (editingCard === 'life' && (tempConfig.life_reward === '' || tempConfig.life_reward == null || tempConfig.life_reward === undefined || tempConfig.life_reward < 1)) {
      setFieldErrors({ life_reward: 'Minimum 1 life is required' });
      setSaving(false);
      return;
    }

    const cardKeys = CARD_FIELD_KEYS[editingCard];
    if (!cardKeys || cardKeys.length === 0) {
      Toast.error('Unknown card type');
      return;
    }

    setSaving(true);
    try {
      // Only send keys for the card being edited
      const cleanPayload = {};
      cardKeys.forEach((key) => {
        let value = tempConfig[key];
        if (key === 'life_reward') {
          if (value === '' || value == null || value === undefined || value < 1) {
            value = 1;
          }
        } else if (value === '' || value == null || value === undefined) {
          value = 0;
        }
        cleanPayload[key] = value;
      });

      await updateKarmaCoinsConfig(cleanPayload);

      setConfig((prev) => {
        const next = { ...prev, ...cleanPayload };
        karmaCoinsCache.config = next;
        return next;
      });
      Toast.success('Karma coins configuration updated successfully');
      setEditingCard(null);
      setTempConfig({});
      setIsFirstTime(false);
      setFieldErrors({});
    } catch (error) {
      Toast.error(error.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    const errors = { ...fieldErrors };
    
    // Validation for "Lives to Add" field (life_reward)
    if (field === 'life_reward') {
      // Remove non-numeric characters
      const valueString = value.toString().replace(/[^0-9]/g, '');
      // If empty, store empty string to show empty field, otherwise parse the integer
      if (valueString === '') {
        errors[field] = 'Minimum 1 life is required';
        setFieldErrors(errors);
        setTempConfig(prev => ({
          ...prev,
          [field]: ''
        }));
        return;
      }
      const numValue = parseInt(valueString, 10) || 0;
      if (numValue < 1) {
        errors[field] = 'Minimum 1 life is required';
        setFieldErrors(errors);
        setTempConfig(prev => ({
          ...prev,
          [field]: ''
        }));
        return;
      } else if (numValue > 5) {
        errors[field] = 'Cannot add more than 5 lives';
        setFieldErrors(errors);
        // Cap the value at 5 instead of storing invalid value
        setTempConfig(prev => ({
          ...prev,
          [field]: 5
        }));
        return;
      } else {
        delete errors[field];
      }
      setFieldErrors(errors);
      setTempConfig(prev => ({
        ...prev,
        [field]: numValue
      }));
      return;
    }
    
    // For all other fields: limit to 6 digits - stop input after 6 digits
    // Remove non-numeric characters first
    let valueString = value.toString().replace(/[^0-9]/g, '');
    
    // If empty after removing non-numeric, store empty string to show empty field
    if (valueString === '') {
      delete errors[field];
      setFieldErrors(errors);
      setTempConfig(prev => ({
        ...prev,
        [field]: ''
      }));
      return;
    }
    
    // Check if trying to enter more than 6 digits and show error
    if (valueString.length > 6) {
      errors[field] = 'cannot exceed 6 digits';
      // Truncate to 6 digits maximum - prevent entering more than 6 digits
      valueString = valueString.substring(0, 6);
      Toast.error('Cannot enter more than 6 digits');
    }
    
    // Parse the value - parseInt automatically removes leading zeros (e.g., "023" becomes 23)
    const numValue = parseInt(valueString, 10) || 0;
    
    // Check if parsed value exceeds 999999 (6 digits max)
    if (numValue > 999999) {
      errors[field] = 'cannot exceed 6 digits';
      Toast.error('Cannot enter more than 6 digits');
      // Set to max allowed value
      setFieldErrors(errors);
      setTempConfig(prev => ({
        ...prev,
        [field]: 999999
      }));
      return;
    }
    
    // Clear any previous errors when value is valid (6 digits or less)
    delete errors[field];
    setFieldErrors(errors);
    setTempConfig(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const renderCard = (title, description, fields, cardType) => {
    const isEditing = editingCard === cardType;
    const currentData = isEditing ? tempConfig : config;
    
    // Check if all fields for this card are empty (0, null, undefined, or empty string)
    // If all are empty, show "Add", otherwise show "Edit"
    const isCardFirstTime = fields.every(field => {
      const value = currentData[field.key];
      return value === 0 || value === '' || value == null || value === undefined;
    });

    return (
      <div className="bg-white rounded-2xl p-6 shadow-md border transition-all">

        {/* ===== HEADER WITH ACTION BUTTONS ===== */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-black">{title}</h3>

          {/* ===== TOP RIGHT ACTIONS ===== */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing ? (
              <>
                {/* Save */}
                <button
                  onClick={handleSave}
                  className="h-9 px-6 text-sm font-bold text-white rounded-lg bg-brand"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>

                {/* Close icon */}
                <button
                  onClick={handleCancel}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-100 text-gray-500"
                  title="Close"
                  disabled={saving}
                >
                  <HiX className="w-5 h-5" />
                </button>
              </>
            ) : (
              <button
                onClick={() => handleEdit(cardType)}
                className="h-9 px-6 text-sm font-bold text-white bg-brand rounded-lg"
              >
                {isCardFirstTime ? 'Add' : 'Edit'}
              </button>
            )}
          </div>
        </div>

        <p className="text-sm text-gray-400 leading-relaxed min-h-[56px] mb-6">
          {description}
        </p>

        {/* ===== INPUTS (FULL WIDTH / 50%) ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5 w-full">
              <label className="text-sm font-bold -mt-3 sm:-mt-6 text-gray-500">
                {field.label}
              </label>

              <div className="relative w-full">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={field.key === 'life_reward' ? 1 : 6}
                  value={
                    currentData[field.key] === '' || currentData[field.key] == null || currentData[field.key] === undefined
                      ? ''
                      : currentData[field.key]
                  }
                  

                  onDoubleClick={() => {
                    if (!isEditing) {
                      setEditingCard(cardType);
                      setTempConfig({ ...config });
                    }
                  }}

                  onChange={(e) => {
                    if (editingCard === cardType) {
                      handleChange(field.key, e.target.value);
                    }
                  }}

                  onKeyDown={(e) => {
                    if (editingCard === cardType && field.key !== 'life_reward') {
                      const currentValue = e.target.value.toString().replace(/[^0-9]/g, '');
                      const selectionLength = e.target.selectionEnd - e.target.selectionStart;
                      const isNumericKey = /^[0-9]$/.test(e.key);
                      
                      // Prevent input if already 6 digits and user is trying to add more (not replacing selected text)
                      if (currentValue.length >= 6 && isNumericKey && selectionLength === 0 &&
                          !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(e.key) &&
                          !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        Toast.error('Cannot enter more than 6 digits');
                      }
                    } else if (editingCard === cardType && field.key === 'life_reward') {
                      const currentValue = e.target.value.toString().replace(/[^0-9]/g, '');
                      const selectionLength = e.target.selectionEnd - e.target.selectionStart;
                      const isNumericKey = /^[0-9]$/.test(e.key);
                      const isSpecialKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(e.key);
                      
                      // Prevent entering 0, 6, 7, 8, 9 as first character (only 1-5 allowed)
                      if (currentValue.length === 0 && ['0', '6', '7', '8', '9'].includes(e.key) && selectionLength === 0 && !isSpecialKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        return;
                      }
                      
                      // Prevent input if already 1 digit and user is trying to add more (not replacing selected text)
                      // Maximum is 5, so only 1 digit allowed
                      if (currentValue.length >= 1 && isNumericKey && selectionLength === 0 && !isSpecialKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                      }
                    }
                  }}

                  readOnly={editingCard !== cardType}
                  className={`w-full h-11 px-3 bg-[#ECECEC] border-2 rounded-lg text-black font-semibold
    outline-none focus:outline-none
    ring-0 focus:ring-0
    focus:border-brand/30
    active:outline-none active:ring-0
    ${editingCard === cardType
                      ? fieldErrors[field.key] 
                        ? 'border-red-500' 
                        : 'border-brand/30'
                      : 'opacity-90 cursor-pointer border-transparent'
                    }`}
                />

                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-brand/60">
                  <FaCoins className="w-4 h-4" />
                </div>
              </div>
              {isEditing && fieldErrors[field.key] && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors[field.key]}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };


  return (
    <div className="flex h-screen bg-primary overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-primary sticky top-2">
          <div className="flex items-center justify-between p-4 px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-black/5 text-black"
              >
                <HiMenu className="w-6 h-6" />
              </button>
              <h1 className="text-xl sm:text-3xl font-black text-black">
                Offers & Pricing
              </h1>
            </div>
          </div>
        </div>

        {/* Dropdown */}
        <div className="px-8 pt-3 sm:pt-8 relative z-20">
          <div className="relative inline-block text-left">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="inline-flex items-center justify-between w-[280px]
              rounded-xl bg-white px-4 py-2.5 text-sm font-bold
              text-black shadow-sm border"            >
              <span className="text-left">{tabs.find(t => t.id === activeTab)?.name}</span>
              <HiChevronDown className={`flex-shrink-0 h-5 w-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
                <div className="absolute left-0 mt-2 w-full rounded-xl bg-white shadow-2xl border z-20">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.set('tab', tab.id);
                        setSearchParams(newSearchParams);
                        setIsDropdownOpen(false);
                        setEditingCard(null);
                      }}
                      className={`flex w-full px-6 py-3 text-sm ${activeTab === tab.id
                        ? 'bg-brand/10 text-brand font-bold'
                        : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Cards */}
        <div className="flex-1 p-6 pb-12 overflow-y-auto relative">
          {loading && activeTab !== 'time-plans' ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center bg-primary">
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : null}
          {activeTab === 'time-plans' ? (
            <KrishnaTimePlansTab />
          ) : !loading && (
            <div className="grid grid-cols-1 xl:grid-cols-2 3xl:grid-cols-3 gap-6 max-w-[1700px] mx-auto">

            {activeTab === 'rewards' && (
              <>
              
                {renderCard('Points Reward', 'Configure the activity points required and set the number of coins users earn as a reward.',
                  [
                    { key: 'points_threshold', label: 'Rewards Per Points' },
                    { key: 'points_reward', label: 'Reward Coins' }
                  ], 'points')}

                {renderCard('Unit Completion Reward', 'Award coins to users when they complete the defined number of units to boost learning consistency.',
                  [
                    { key: 'lessons_threshold', label: 'Rewards Per Units' },
                    { key: 'lessons_reward', label: 'Reward Coins' }
                  ], 'lessons')}

                {renderCard('Audio Content Reward', 'Grant coins for completing audio lessons and encourage users to stay engaged with audio content.',
                  [
                    { key: 'audio_lessons_threshold', label: 'Rewards per Audio Lesson' },
                    { key: 'audio_lessons_reward', label: 'Reward Coins' }
                  ], 'audio')}
                
                {renderCard('Default Karma Coins', 'Choose the how many karma coins users receive by default when they are not eligible for any other reward.',
                  [{ key: 'not_eligible_reward', label: 'Karma Coins' }],
                  'not_eligible_reward')}
              </>
            )}

            {activeTab === 'referral' && (
              <>
                {renderCard('Referral Rewards', 'Reward users with coins when they successfully invite new members to the platform',
                  [{ key: 'referral_reward', label: 'Coins Per Referral' }],
                  'referral')}

                {renderCard('Life Refill Settings', 'Configure the number of ads a user needs to watch to refill their lives and how many lives they receive.',
                  [
                    { key: 'life_refill_reward', label: 'Ads to Watch' },
                    { key: 'life_reward', label: 'Lives to Add' }
                  ],
                  'life')}

                {renderCard('Full Life Refill Cost', 'Set the coin amount users must pay to instantly refill their lives to the maximum.',
                  [{ key: 'life_coins_reward', label: 'Coins Cost' }],
                  'full_life')}

                {renderCard('Double Coins Event', 'Configure the number of chapters users need to complete to receive double coins reward.',
                  [
                    { key: 'double_coins_event_required_chapter', label: 'Required Chapters' },
                     { key: 'double_coins_event_redumption', label: 'Reward Coins' }
                  ],
                  'double_coins_event')}
              </>
            )}

            {activeTab === 'leaderboard' && (
              <>
                {renderCard('Leaderboard Rank 1', 'Reward the top performer each month with bonus coins for outstanding achievement.',
                  [{ key: 'leaderboard_rank_1_reward', label: 'Winner Coins' }], 'rank1')}
                {renderCard('Leaderboard Rank 2', 'Recognize the runner-up with coins for their excellent performance on the leaderboard.',
                  [{ key: 'leaderboard_rank_2_reward', label: 'Silver Coins' }], 'rank2')}
                {renderCard('Leaderboard Rank 3', 'Celebrate third place with a coin reward to encourage continued participation.',
                  [{ key: 'leaderboard_rank_3_reward', label: 'Bronze Coins' }], 'rank3')}
                {renderCard('Top 10 Participation', 'Reward other top-ranked users with participation coins to keep competition strong and motivating.',
                  [{ key: 'leaderboard_others_reward', label: 'Participation Coins' }], 'others')}
              </>
            )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KarmaCoins;
